import { DbCrudHelper, ErrorToSuppress } from "@app/shared/services/db-crud-helper";
import { catchError, map, take } from "rxjs/operators";
import { User, UserData, UserHelper } from "../model/user";
import { ClareUserPermission, ROLE_NAMES } from "../model/user-role";

import { Injectable } from "@angular/core";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { SsoUserInfo } from "@app/auth/model/auth-user";
import { ClareWSCrudHelperService } from "@app/shared/services/clare-ws-crud-helper.service";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { Observable } from "rxjs";

interface GetEntityUsersResult {
    account_number: string | null;
    create_time: string;
    email: string;
    entity_id: number | null;
    first_name: string | null;
    git_id: string | null;
    id: number;
    invite_code: string | null;
    last_name: string | null;
    notes: string | null;
    role: {
        name: ROLE_NAMES;
        permissions: ClareUserPermission[];
    };
    role_id: number;
    userinfo1: number;
}

@Injectable({
    providedIn: "root",
})
export class UserDbService {
    helper = new UserHelper();
    wsHelper = new ClareWSCrudHelperService(this.tsLoggingService, this.crudHelper, this.helper);

    private readonly PERMISSION_DENIED_ERROR = ErrorToSuppress.PERMISSION_DENIED_ERROR;

    constructor(private crudHelper: DbCrudHelper, protected tsLoggingService: AuditLogService) {}

    /**
     * @description Fetches or create a user based on whether the email is already in existence in the DB or not
     * @param uuid
     * @param {User} newUser the user to create or invite
     * @returns {Observable<{ invitedUser: User; isNewUser: boolean }>} the invited user and whether it is a new user or not
     */
    getOrCreateUserByEmail(newUser: User, uuid): Observable<{ invitedUser: User }> {
        const userData = this.helper.toDbObject(newUser);
        const userDataWithUuid = Object.assign(userData, { uuid });
        return this.wsHelper
            .createFromAny({
                endpoint: "SaveUserForClaresystem",
                data: userDataWithUuid,
                logData: { newUser: userDataWithUuid },
            })
            .pipe(
                map(invitedUser => ({ invitedUser: this.helper.fromDbObject(invitedUser) })),
                take(1)
            );
    }

    /**
     * @description Checks to see if the email address for the user is already in use.
     * @param {User} user the user to check
     * @returns {Observable<UserData[] | DataServiceError>} an empty array if the user does not exist
     * or a data service error if the user exists
     */
    checkForEmailExistence(user: User): Observable<UserData[] | DataServiceError<any>> {
        const emailInUseMessage = "The email address you're attempting to use is already in our system.";
        return this.getUserByEmail(user.email).pipe(
            map((result: UserData) => {
                if (result) {
                    throw new DataServiceError(emailInUseMessage);
                }

                return [];
            }),
            catchError((error: DataServiceError<{ error: string }>) => {
                // permission denied error thrown when user exists, but in a different company
                if (error.error === this.PERMISSION_DENIED_ERROR) {
                    throw new DataServiceError(emailInUseMessage);
                } else {
                    throw new DataServiceError(error.error);
                }
            })
        );
    }

    /**
     * @description Searches the database for a staff user with the given email address
     * @param {string} email the user's email
     * @returns {Observable<User[]>} the array of staff users for the entity
     */
    getStaffByEmail(email: string): Observable<User[]> {
        return this.wsHelper
            .retrieveSingle({
                endpoint: "GetStaffByEmail",
                data: { email },
                // suppressing permission denied error as this is thrown when staff member is found, but exists in different company
                // error is still thrown, but snackbar and console are suppressed
                errorsToSuppress: [this.PERMISSION_DENIED_ERROR],
            })
            .pipe(map(userData => (userData ? [this.helper.fromDbObject(userData)] : [])));
    }

    /**
     * @description Fetches staff/user (not customers) list from an entity in the database
     * @param payload the entity
     * @param {number} payload.entityId the entity id to get the users for
     * @returns {Observable<{ users: User[] }>} the array of matching users
     */
    getEntityUsers$({ entityId }: { entityId: number }): Observable<{ users: User[] }> {
        return this.wsHelper
            .retrieve({
                endpoint: "GetEntityStaffs",
                data: {
                    id: entityId,
                },
            })
            .pipe(map(dataArray => ({ users: dataArray?.map(data => this.helper.fromDbObject(data)) })));
    }

    /**
     * @description Fetches company admin (not customers) list from an entity in the database
     * @param payload the entity
     * @param {number} payload.entityId the entity id to get the users for
     * @returns {Observable<{ users: User[] }>} the array of matching users
     */
    getEntityCompanyAdmins$({ entityId }: { entityId: number }): Observable<{ users: User[] }> {
        return this.wsHelper
            .retrieve({
                endpoint: "GetEntityCompanyAdmins",
                data: {
                    id: entityId,
                },
            })
            .pipe(map(dataArray => ({ users: dataArray?.map(data => this.helper.fromDbObject(data)) })));
    }

    getCustomerByEmail(email: string): Observable<User | undefined> {
        return this.getUserByEmail(email).pipe(
            map((result: UserData) => {
                if (result) {
                    return this.helper.fromDbObject(result);
                }

                return undefined;
            })
        );
    }

    /**
     * @description Fetches staff users from the DB for a given entityId. This will also return users on child entities.
     * @param param0 the param
     * @param {number} param0.entityId the entity ID of the users to fetch
     * @returns {Observable<{ users: ClareDbCollection<User> }>} the collection of users
     */
    getEntityAllStaffs({ entityId }: { entityId: number }) {
        return this.wsHelper.retrieve({
            endpoint: "GetEntityAllStaffs",
            data: { id: entityId },
        });
    }

    /**
     * @description Fetches customer from the DB for a given entityId. This will also return users on child entities.
     * @param param0 the param
     * @param {number} param0.entityId the entity ID of the users to fetch
     * @returns {Observable<{ users: ClareDbCollection<User> }>} the collection of users
     */
    getEntityAllCustomers({ entityId }: { entityId: number }) {
        return this.wsHelper.retrieve({
            endpoint: "GetEntityAllCustomers",
            data: { id: entityId },
        });
    }

    /**
     * @description Fetches all users and their roles from the database.
     * @returns {Observable<{ users: ClareDbCollection<User> }>} the users
     */
    getAllUsers() {
        return this.wsHelper.retrieve({
            endpoint: "GetAllUsers",
        });
    }

    /**
     * @description Fetches the user from the database.
     * @param param0 the user object
     * @param {number} param0.userId The ID of the user to fetch
     * @returns {Observable<{ user: User }>} the user info
     */
    getUser({ userId }: { userId: number }): Observable<{ user: User }> {
        return this.wsHelper
            .retrieveSingle({
                endpoint: "GetUser",
                data: {
                    id: userId,
                },
            })
            .pipe(map(user => ({ user: this.helper.fromDbObject(user) })));
    }

    /**
     * @description Creates the user in the database and logs the entry in the TS DB.
     * @param param0 the user object
     * @param log
     * @param {User} param0.user The user info to be created
     * @returns {Observable<{ user: User }>} the created user info
     */
    createUser({ user }: { user: User }, log: any = {}): Observable<{ user: User }> {
        const { auditLogName, ...logData } = log;
        return this.wsHelper
            .create({
                endpoint: "CreateUser",
                data: user,
                logData,
                auditLogName,
            })
            .pipe(map(newUser => ({ user: this.helper.fromDbObject(newUser) })));
    }

    /**
     * @description Deletes the user in the database and logs the entry in the TS DB.
     * @param param0 the user object
     * @param log
     * @param {User} param0.user The user info to be deleted
     * @returns {Observable<{ user: User }>} the deleted user info
     */
    deleteUser({ user }: { user: User }, log: any = {}): Observable<{ user: User }> {
        const { auditLogName, ...logData } = log;
        return this.wsHelper
            .delete({
                endpoint: "DeleteUser",
                id: user.id,
                auditLogName,
                logData,
            })
            .pipe(map(_ => ({ user })));
    }

    /**
     * @description Updates the user in the database. We had issues with the gitkit_id and email addresses being overwritten
     * on users, so we added measures below to remove those properties if they are already set in the DB. The email address
     * @param log.user
     * @param log
     * can be updated as long as the gitkit_id is not set yet (this means they have not created a Firebase login yet).
     * @param param0 the user object
     * @param {User} param0.user The user info to be updated
     * @returns {Observable<{ user: User }>} the updated user info
     */
    updateUser({ user }: { user: User }, log: any = {}): Observable<{ user: User }> {
        const { auditLogName, ...logData } = log;
        return this.wsHelper
            .update({
                endpoint: "UpdateUser",
                data: user,
                auditLogName,
                logData,
            })
            .pipe(map(() => ({ user })));
    }

    /**
     * @description look up user by email
     * @param email email for looking up
     * @returns { Observable<UserData> } user data
     */
    getUserByEmail(email: string): Observable<UserData> {
        return this.wsHelper
            .retrieveSingle({
                endpoint: "GetUserByEmail",
                data: {
                    email,
                },
                // suppressing permission denied error as this is thrown when user is found, but exists in different company
                // error is still thrown, but snackbar and console are suppressed
                errorsToSuppress: [this.PERMISSION_DENIED_ERROR],
            })
            .pipe(map((user: UserData) => user)); // programatically cast the type to UserData
    }

    /**
     * @description get Customer location
     * @param customerId customer id
     * @returns { Observable<GetEntityUsersResult[] | null> } customers
     */
    getLocationsByCustomer(customerId: number) {
        return this.crudHelper.makeRequest<GetEntityUsersResult[] | null>("GetClaresystemsByUserinfoID", { id: customerId }, [
            this.PERMISSION_DENIED_ERROR,
        ]);
    }

    /**
     * @description get Customer location by system id
     * @param systemId system id
     * @returns { Observable<GetEntityUsersResult[] | null> } customers
     */
    getLocationsBySystemId(systemId: number) {
        return this.crudHelper.makeRequest<UserData[] | null>("GetUserinfosByClaresystemID", { id: systemId }, [
            this.PERMISSION_DENIED_ERROR,
        ]);
    }

    getLoggedInUserInfo(gitId: string) {
        return this.crudHelper.makeRequest<SsoUserInfo | null>("GetUserByGitID", { git_id: gitId });
    }
}
